import React, { Dispatch, SetStateAction, createContext, useContext, useState } from 'react';
import type { AxiosResponse } from 'axios';

import { LOCAL_STORAGE_KEYS } from '@/constants';

import axios from '@axios';

type LoginProps = {
  user: User;
  token: string;
};

export interface TPermissions {
  action?: string;
  path: string;
  name?: string;
  description?: string;
  can: string[];
  id?: number | string;
  icon?: string;
}

interface IAuthenticateContext {
  isLoggedIn: boolean;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  setAvailableMenus: Dispatch<SetStateAction<string[]>>;
  setAuth: Dispatch<SetStateAction<User | undefined>>;
  logout(): Promise<void>;
  login: (props: LoginProps) => void;
  availableMenus: string[];
  Auth: User;
  closingSession: boolean;
  changePassword: (payload: any) => Promise<AxiosResponse<{ message: string }>>;
}

export const AuthenticationContext = createContext<IAuthenticateContext | null>(null);

export const getUserFromLocalStorage = () => {
  try {
    const user = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
    if (!user) return;
    return JSON.parse(user);
  } catch (error) {
    clearKeysLocalStorage();
    return;
  }
};

export const clearDataAuthenticate = () => {
  localStorage.clear();
};

export const clearKeysLocalStorage = () => {
  Object.values(LOCAL_STORAGE_KEYS).forEach(localStorage.removeItem);
};

const AuthenticationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [availableMenus, setAvailableMenus] = useState<string[]>([]);
  const [closingSession, setClosingSession] = useState<boolean>(false);
  const [Auth, setAuth] = useState(getUserFromLocalStorage);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    try {
      const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
      return !!token;
    } catch (error) {
      clearKeysLocalStorage();
      return false;
    }
  });

  const logout = async () => {
    await httpLogout();
    clearKeysLocalStorage();
  };

  const login = ({ user, token }: LoginProps) => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));
      setAuth(user);
      setIsLoggedIn(true);
    } catch (error) {
      console.error({ error });
    }
  };

  const httpLogout = async () => {
    try {
      setClosingSession(true);
      await axios.post('/logout');
    } catch (error) {
    } finally {
      setClosingSession(false);
    }
  };

  const changePassword = async (payload: any): Promise<AxiosResponse<{ message: string }>> => {
    try {
      return await axios.post('/change-password', payload);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    setIsLoggedIn,
    isLoggedIn,
    logout,
    login,
    setAvailableMenus,
    availableMenus,
    setAuth,
    Auth,
    closingSession,
    changePassword,
  };

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

export const useAuthenticate = () => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error(
      'The context AuthenticationContext no use AuthenticationContextProvider or undefined',
    );
  }
  return context;
};

export default AuthenticationContextProvider;
